import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {TimelineRowPassthrough} from '@github-ui/timeline-items/TimelineRowPassthroughContext'

import styles from './event-row.module.css'

const FAILED_LOAD_ITEM_ID = 'failed-timeline-item'

/**
 * Row-level treatments issue-viewer restores on a non-comment event's
 * `Timeline.Item` `<li>` under `primer_react_timeline_list_semantics`. Before the
 * flag, `TimelineRowBorder` and `wrapElement` carried these around the event; the
 * flag path can no longer wrap the event (an `<ol>` accepts only `<li>` children),
 * so it feeds them through `TimelineRowPassthroughContext` instead.
 *
 * The values mirror the flag-off event row exactly:
 *  - `rowClassName`: sets the `--timeline-row-indent-left` custom property that
 *    holds the collapsed left indent (see event-row.module.css). The class tracks
 *    `issue_inline_avatars` with the same call `wrapElement` makes, so the badge
 *    and body keep the same horizontal position in every state. The margin that
 *    reads the property lives in timeline-items, where it beats Primer's
 *    `Timeline.Item` margin by source order.
 *  - `rowIndent`: opts the `<li>` into that timeline-items margin.
 *  - `rowDataAttributes`: `data-timeline-event-id`, `data-highlighted-event`
 *    (always `false` for events, which were never highlight-gated), the
 *    `timeline-row-border-<id>` test id, and the `data-wrapper-timeline-id`
 *    anchor consumed by focus-after-load and Safari scroll preservation.
 *  - `isGroupStart`: marks a group boundary, when the timeline requests one. Flag-off
 *    this was a full-width divider strip above the row; flag-on it becomes top
 *    padding on the `<li>` (see `.Timeline_Item_groupStart` in timeline-items).
 *
 * `data-timeline-event-id` uses the same literal as
 * `VALUES.timeline.dataTimelineEventId`; the literal keeps the key assignable to
 * the `data-${string}` template-literal type on `rowDataAttributes`.
 */
export function computeEventRowPassthrough({
  itemId,
  addDivider,
}: {
  itemId: string
  addDivider: boolean
}): TimelineRowPassthrough {
  return {
    rowClassName: isFeatureEnabled('issue_inline_avatars') ? styles.rowInlineAvatars : styles.eventRow,
    rowDataAttributes: {
      'data-timeline-event-id': itemId,
      'data-highlighted-event': false,
      'data-testid': `timeline-row-border-${itemId}`,
      'data-wrapper-timeline-id': itemId,
    },
    isGroupStart: addDivider,
    rowIndent: true,
  }
}

/**
 * Row-level treatments for the failed-load row under
 * `primer_react_timeline_list_semantics`. The failed-load row renders directly
 * (never through `wrapElement`), so flag-off it carried a `TimelineRowBorder` with
 * a divider, `.default` padding, `data-timeline-event-id`, and the test id, but
 * NOT `data-highlighted-event` (its `isHighlighted` was undefined, so React
 * dropped the attribute) and NOT `data-wrapper-timeline-id` (no wrap element and
 * no 56px leading indent). This mirrors that shape exactly.
 */
export function computeFailedLoadRowPassthrough(): TimelineRowPassthrough {
  return {
    rowClassName: isFeatureEnabled('issue_inline_avatars') ? styles.rowInlineAvatars : styles.failedLoadRow,
    rowDataAttributes: {
      'data-timeline-event-id': FAILED_LOAD_ITEM_ID,
      'data-testid': `timeline-row-border-${FAILED_LOAD_ITEM_ID}`,
    },
    isGroupStart: true,
    rowIndent: true,
  }
}
